import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  FormHelperText,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { AddCircleOutline, DeleteOutline } from '@mui/icons-material';

import { claimsObjectToRows, rowsToClaimsObject } from './utils';

export default function RequiredClaimsField({
  value,
  onChange,
  disabled = false,
  stacked = false,
}) {
  const [rows, setRows] = useState(() => claimsObjectToRows(value));

  useEffect(() => {
    const from_parent = claimsObjectToRows(value);
    if (from_parent.length > 0) {
      setRows(from_parent);
    }
  }, [value]);

  const updateRows = (next_rows) => {
    setRows(next_rows);
    onChange(rowsToClaimsObject(next_rows));
  };

  const addRow = () => {
    updateRows([...rows, { claim: '', values: [] }]);
  };

  const removeRow = (idx) => {
    const next = [...rows];
    next.splice(idx, 1);
    updateRows(next);
  };

  const updateRow = (idx, patch) => {
    const next = [...rows];
    next[idx] = { ...next[idx], ...patch };
    updateRows(next);
  };

  return (
    <Box>
      <Stack direction='row' spacing={2} alignItems='center' sx={{ mb: 1 }}>
        <Typography variant='body1' fontWeight='medium'>
          Required Claims (recommended)
        </Typography>
        <Button
          variant='outlined'
          size='small'
          startIcon={<AddCircleOutline />}
          onClick={addRow}
          disabled={disabled}
        >
          Add Claim
        </Button>
      </Stack>

      {rows.length === 0 ? (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
          Optional. Add a claim name and the allowed values for that claim.
        </Typography>
      ) : null}

      <Stack spacing={2}>
        {rows.map((row, idx) => (
          <Grid container spacing={2} key={idx} alignItems='flex-start'>
            <Grid size={stacked ? 4 : { xs: 12, md: 3 }}>
              <TextField
                label='Claim'
                value={row.claim || ''}
                onChange={(e) => updateRow(idx, { claim: e.target.value })}
                fullWidth
                disabled={disabled}
                placeholder='e.g. scp'
              />
            </Grid>
            <Grid size={stacked ? 8 : { xs: 12, md: 8 }}>
              {stacked ? (
                <Box sx={{ width: '100%' }}>
                  <Stack
                    direction='row'
                    spacing={0.5}
                    alignItems='center'
                    sx={{ width: '100%' }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Autocomplete
                        multiple
                        freeSolo
                        options={[]}
                        value={row.values || []}
                        onChange={(event, new_values) => {
                          const next_values = new_values
                            .map((item) =>
                              typeof item === 'string' ? item : String(item),
                            )
                            .map((item) => item.trim())
                            .filter((item) => item);
                          updateRow(idx, { values: next_values });
                        }}
                        disabled={disabled}
                        disablePortal
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='Values'
                            placeholder='Type a value and press Enter'
                          />
                        )}
                      />
                    </Box>
                    <IconButton
                      onClick={() => removeRow(idx)}
                      disabled={disabled}
                      aria-label='Remove claim'
                    >
                      <DeleteOutline />
                    </IconButton>
                  </Stack>
                  <FormHelperText>
                    Add allowed values as tags. Press Enter after each one.
                  </FormHelperText>
                </Box>
              ) : (
                <>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={[]}
                    value={row.values || []}
                    onChange={(event, new_values) => {
                      const next_values = new_values
                        .map((item) =>
                          typeof item === 'string' ? item : String(item),
                        )
                        .map((item) => item.trim())
                        .filter((item) => item);
                      updateRow(idx, { values: next_values });
                    }}
                    disabled={disabled}
                    disablePortal
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Values'
                        placeholder='Type a value and press Enter'
                      />
                    )}
                  />
                  <FormHelperText>
                    Add allowed values as tags. Press Enter after each one.
                  </FormHelperText>
                </>
              )}
            </Grid>
            {stacked ? null : (
              <Grid size={{ xs: 12, md: 1 }}>
                <IconButton
                  onClick={() => removeRow(idx)}
                  disabled={disabled}
                  aria-label='Remove claim'
                >
                  <DeleteOutline />
                </IconButton>
              </Grid>
            )}
          </Grid>
        ))}
      </Stack>
    </Box>
  );
}
